<script setup lang="ts">
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { useThemePreferencesSync } from '@core/composable/useThemePreferencesSync'
import { darkenHex, hexToRgb } from '@core/utils/colorConverter'
import { useLanguageStore } from '@/stores/language'
import { usePlatformBranding } from '@/composables/usePlatformBranding'

const vuetifyTheme = useTheme()
const { global } = vuetifyTheme
const { locale } = useI18n()
const languageStore = useLanguageStore()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()

watch(locale, val => {
  languageStore.fetchTranslations(val)
})

// Colore brand del tenant whitelabel applicato al tema Vuetify globale
// (stessa tecnica di TheCustomizer.vue: muta direttamente
// vuetifyTheme.themes.value.<light|dark>.colors.primary) - cosi' bottoni,
// link e l'evidenziazione della sidebar attiva (--v-global-theme-primary
// qui sotto e' gia' derivato da colors.primary) seguono il colore del
// tenant ovunque nel pannello, non solo dove esplicitamente cablato.
// Applicato SOLO in whitelabel: fuori da whitelabel il tema resta quello
// statico di default (staticPrimaryColor), invariato per tutti gli altri
// tenant - non usiamo/tocchiamo la personalizzazione manuale via cookie
// di TheCustomizer, che qui viene intenzionalmente sovrascritta perche'
// la coerenza col brand del reseller ha priorita'.
const { isWhitelabel, primaryColor, loadPlatformBranding } = usePlatformBranding()

onMounted(() => {
  loadPlatformBranding()
})

watch([isWhitelabel, primaryColor], ([whitelabel, color]) => {
  if (!whitelabel || !color)
    return

  const darkenColor = darkenHex(color, 0.1)

  for (const themeName of ['light', 'dark'] as const) {
    vuetifyTheme.themes.value[themeName].colors.primary = color
    vuetifyTheme.themes.value[themeName].colors['primary-darken-1'] = darkenColor
  }
}, { immediate: true })

// Preferenze Theme Customizer (tema, skin, colore, layout...) per-utente:
// applicate dal DB al mount, poi risalvate ad ogni cambiamento - cosi'
// restano identiche da qualunque browser/dispositivo con lo stesso
// account, non solo nei cookie del browser corrente.
const { fetchFromServer: fetchThemePreferences, startWatching: watchThemePreferences } = useThemePreferencesSync(isWhitelabel)

onMounted(async () => {
  await fetchThemePreferences()
  watchThemePreferences()
})
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />
      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>
